import type { MiloProposedAction, MiloTurnResponse } from "@keydials/api-client";

/**
 * The Milo panel's conversation state (KEY-704), kept apart from React so it can be tested as plain data.
 *
 * The panel shows what was said on both sides plus the one write Milo may be waiting on. The server holds the
 * real transcript; this is only what the person sees, so it can be rebuilt from `miloConversation` after a
 * reload and dropped without loss.
 */

export interface MiloChatMessage {
  role: "user" | "milo";
  text: string;
  /** A refusal or failure shown in Milo's voice but styled as an error, so it isn't mistaken for an answer. */
  error?: boolean;
}

export interface MiloChatState {
  conversationId: string | null;
  messages: MiloChatMessage[];
  proposed: MiloProposedAction | null;
  turnsLeft: number | null;
}

export const emptyMiloChat: MiloChatState = { conversationId: null, messages: [], proposed: null, turnsLeft: null };

/** What the person just said goes on screen before the reply comes, so the wait reads as Milo thinking. */
export function withUserMessage(state: MiloChatState, text: string): MiloChatState {
  return { ...state, messages: [...state.messages, { role: "user", text }], proposed: null };
}

export function withReply(state: MiloChatState, reply: MiloTurnResponse): MiloChatState {
  return {
    conversationId: reply.conversationId,
    messages: [...state.messages, { role: "milo", text: reply.reply }],
    proposed: reply.proposedAction ?? null,
    turnsLeft: reply.turnsLeft,
  };
}

/** A refusal keeps the conversation: the pool being spent is not a reason to lose what was said. */
export function withError(state: MiloChatState, text: string): MiloChatState {
  return { ...state, messages: [...state.messages, { role: "milo", text, error: true }], proposed: null };
}

/** What the pool line under the title says, or null when there is nothing to say (billing off). */
export function poolLine(status: { allowance?: number | null; used?: number | null; packs?: number | null; topUpRemaining?: number | null; plus?: boolean } | null): string | null {
  if (!status || status.allowance == null || status.used == null) return null;
  // KEY-847: with no pack the household has free Milo, not "no Milo".
  if (status.plus === false || status.allowance === 0) return "Free Milo";
  const left = Math.max(0, status.allowance - status.used);
  // Top-up credits (KEY-717) are spent after the month's own, and outlive it, so they read as a separate figure.
  const topUp = status.topUpRemaining && status.topUpRemaining > 0 ? ` + ${status.topUpRemaining} top-up` : "";
  return `${left} of ${status.allowance} requests left this month${topUp}`;
}

/**
 * Whether the month is spent and a top-up would help (KEY-717): the pool is usable, its allowance is used up, there
 * are no credits left, and the plan prices a top-up. This is when the panel offers "buy 300 more" under a refusal.
 */
export function offersTopUp(status: { plus?: boolean; allowance?: number | null; used?: number | null; topUpRemaining?: number | null; topUpPence?: number | null } | null): boolean {
  // Only for Milo Plus: a household without it has nothing to top up (KEY-847) — it gets the Milo Plus note instead.
  if (!status || status.plus !== true || status.allowance == null || status.used == null || status.topUpPence == null) return false;
  return status.used >= status.allowance && !(status.topUpRemaining && status.topUpRemaining > 0);
}

/**
 * Whether the panel shows the Milo Plus note above the chat (KEY-847): Milo is on here, but nothing has bought Milo
 * Plus for this household — so the everyday commands work (free Milo) and anything else gets an answer saying what
 * Milo Plus adds. Before KEY-846 this household got an introduction and no chat at all. An older server that says
 * nothing about `plus` is taken as having it: no note.
 */
export function showsPlusNote(status: { enabled: boolean; plus?: boolean } | null): boolean {
  return !!status?.enabled && status.plus === false;
}

/** What a Milo pack costs, as the introduction says it — "£4.99 a month" — or null when no rate card can say. */
export function packPriceLine(status: { packPence?: number | null; packAnnual?: boolean } | null): string | null {
  if (!status || status.packPence == null) return null;
  return `£${(status.packPence / 100).toFixed(2)} a ${status.packAnnual ? "year" : "month"}`;
}

/** The dial the person is looking at, from the URL, so Milo knows what "the MOT" means. */
export function currentInstanceId(pathname: string): string | null {
  const m = /^\/(?:instances|groups)\/([0-9a-f-]{36})(?:\/|$)/i.exec(pathname);
  return m ? m[1] : null;
}
